import React, { useMemo, useState } from "react";
import "../../../css/partner/CommissionsPage.css";
import ToolSidebar from "../../utils/ToolSideBar";

function money(value) {
  const numeric = Number(value || 0);
  return `$${numeric.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function percent(value) {
  if (value === undefined || value === null || value === "") return "-";
  return `${Number(value).toLocaleString()}%`;
}

function date(value, fallback = "-") {
  return value ? new Date(value).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : fallback;
}

function releaseRank(release) {
  if (release.release_type === "deposit" || release.release_type === "full") return 0;
  if (release.release_type === "final") return 1;
  return 2;
}

function statusText(status) {
  return (status || "").replace(/_/g, " ");
}

function overallStatus(releases) {
  const statuses = releases.map((release) => release.status);
  if (statuses.length === 0) return "No Commission";
  if (statuses.every((status) => status === "paid")) return "Paid";
  if (statuses.some((status) => status === "paid")) return "Partially Paid";
  if (statuses.every((status) => status === "approved")) return "Authorized";
  if (statuses.some((status) => status === "approved")) return "Partially Authorized";
  if (statuses.every((status) => status === "pending")) return "Pending";
  if (statuses.some((status) => status === "pending")) return "Partially Pending";
  if (statuses.some((status) => status === "payout_failed")) return "Payout Failed";
  if (statuses.every((status) => status === "awaiting_payment")) return "Awaiting Payment";
  return "In Progress";
}

function groupCommissions(commissions = []) {
  const groups = new Map();
  commissions.forEach((row) => {
    const key = row.commission_id || row.id;
    const current = groups.get(key) || {
      id: key,
      project_id: row.project_id,
      project_title: row.project_title,
      client_name: row.client_name,
      commissionable_amount: row.commissionable_amount,
      commission_percentage: row.commission_percentage,
      total_commission: row.total_commission || row.commission_amount || row.amount,
      releases: [],
    };
    current.total_commission = row.total_commission || current.total_commission;
    current.releases.push(row);
    groups.set(key, current);
  });
  return Array.from(groups.values()).map((group) => ({
    ...group,
    releases: group.releases.sort((a, b) => releaseRank(a) - releaseRank(b)),
    overallStatus: overallStatus(group.releases),
  }));
}

function ReleaseDetail({ release }) {
  const eligible = release.status === "awaiting_payment" ? "TBD" : date(release.payout_eligible_at, "TBD");
  return (
    <div className="commission-release-card">
      <div className="release-card-head">
        <div>
          <span>{release.release_type_label || "Commission Portion"}</span>
          <strong>{money(release.net_amount || release.commission_amount || release.amount)}</strong>
        </div>
        <em className={`status ${release.status}`}>{release.status_label || statusText(release.status)}</em>
      </div>
      <dl>
        <div><dt>Client Payment</dt><dd>{release.payment_status_label || "Not Paid"}</dd></div>
        <div><dt>Payment Date</dt><dd>{date(release.payment_paid_at)}</dd></div>
        <div><dt>Hold Period</dt><dd>{release.payment_paid_at ? "14 days" : "-"}</dd></div>
        <div><dt>Eligible</dt><dd>{eligible}</dd></div>
        <div><dt>Authorized</dt><dd>{date(release.authorized_at)}</dd></div>
        <div><dt>Paid</dt><dd>{date(release.paid_at)}</dd></div>
      </dl>
    </div>
  );
}

export default function CommissionPage({ commissions = [], summary }) {
  const [expanded, setExpanded] = useState({});
  const groups = useMemo(() => groupCommissions(commissions), [commissions]);

  return (
    <div className="commissions-shell">
      <ToolSidebar active="commissions" />
      <div className="commissions-page">
        <div className="commissions-header">
          <h1>Commissions</h1>
          <p>Track total commission value and payment-triggered release status.</p>
        </div>

        <div className="commission-kpis">
          <div className="kpi-card">
            <h3>Total Commission</h3>
            <p>{money(summary?.total_commission_value || summary?.total_commission_earned)}</p>
          </div>
          <div className="kpi-card">
            <h3>Pending</h3>
            <p>{money(summary?.pending || summary?.pending_approval)}</p>
          </div>
          <div className="kpi-card">
            <h3>Authorized</h3>
            <p>{money(summary?.authorized || summary?.pending_payout)}</p>
          </div>
          <div className="kpi-card">
            <h3>Paid</h3>
            <p>{money(summary?.paid)}</p>
          </div>
          <div className="kpi-card">
            <h3>Awaiting Payment</h3>
            <p>{money(summary?.awaiting_payment || summary?.remaining)}</p>
          </div>
          <div className="kpi-card">
            <h3>Earned to Date</h3>
            <p>{money(summary?.earned_to_date || summary?.total_commission_earned)}</p>
          </div>
        </div>

        <div className="commission-table-card">
          <table className="commission-table">
            <thead>
              <tr>
                <th>Project</th>
                <th>Client</th>
                <th>Total Commission</th>
                <th>Rate</th>
                <th>Overall Status</th>
                <th aria-label="Expand" />
              </tr>
            </thead>
            <tbody>
              {groups.length === 0 ? (
                <tr>
                  <td colSpan="6" className="empty">No commissions yet</td>
                </tr>
              ) : (
                groups.map((group) => {
                  const isOpen = Boolean(expanded[group.id]);
                  return (
                    <React.Fragment key={group.id}>
                      <tr
                        className="commission-parent-row"
                        onClick={() => setExpanded((state) => ({ ...state, [group.id]: !state[group.id] }))}
                        aria-expanded={isOpen}
                      >
                        <td>{group.project_title || `Project #${group.project_id}`}</td>
                        <td>{group.client_name || "Not provided"}</td>
                        <td className="amount">{money(group.total_commission)}</td>
                        <td>{percent(group.commission_percentage)}</td>
                        <td><span className={`status overall ${group.overallStatus.toLowerCase().replace(/\s+/g, "-")}`}>{group.overallStatus}</span></td>
                        <td className="expand-cell"><span className="expand-icon">{isOpen ? "v" : ">"}</span></td>
                      </tr>
                      {isOpen && (
                        <tr className="commission-expanded-row">
                          <td colSpan="6">
                            <div className="commission-release-grid">
                              {group.releases.map((release) => <ReleaseDetail key={release.release_id || release.id} release={release} />)}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
